import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, X } from "lucide-react";

type Option = { value: string | number; label: string };
const searchable = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

export function AccountSelect({
  label,
  options,
  value,
  onChange,
  required = true,
  span = false,
}: {
  label: string;
  options: Option[];
  value: string | number | undefined;
  onChange: (value: string) => void;
  required?: boolean;
  span?: boolean;
}) {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(false);
  const [active, setActive] = useState(0);
  const [position, setPosition] = useState<{
    left: number;
    top?: number;
    bottom?: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const selected = options.find((o) => String(o.value) === String(value));
  const terms = searchable(query).trim().split(/\s+/).filter(Boolean);
  const matches = editing
    ? options.filter((o) =>
        terms.every((term) => searchable(o.label).includes(term)),
      )
    : options;

  useEffect(() => {
    input.current?.setCustomValidity(
      editing && query.trim() && !selected
        ? "Selecciona una cuenta de la lista."
        : "",
    );
  }, [editing, query, selected]);
  useEffect(() => {
    if (!open) return;
    function place() {
      const r = input.current!.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - 12;
      const above = r.top - 12;
      const height = Math.min(240, Math.max(below, above));
      const upwards = below < 180 && above > below;
      setPosition({
        left: r.left,
        ...(upwards
          ? { bottom: window.innerHeight - r.top + 4 }
          : { top: r.bottom + 4 }),
        width: r.width,
        maxHeight: upwards ? height : Math.min(240, below),
      });
    }
    function outside(event: PointerEvent) {
      if (
        !root.current?.contains(event.target as Node) &&
        !list.current?.contains(event.target as Node)
      )
        setOpen(false);
    }
    function scroll(event: Event) {
      if (!list.current?.contains(event.target as Node)) place();
    }
    place();
    document.addEventListener("pointerdown", outside);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", scroll, true);
    return () => {
      document.removeEventListener("pointerdown", outside);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", scroll, true);
    };
  }, [open]);
  useEffect(() => {
    if (open)
      document
        .getElementById(`${id}-option-${active}`)
        ?.scrollIntoView({ block: "nearest" });
  }, [open, active, query, id]);

  function choose(option: Option) {
    onChange(String(option.value));
    setEditing(false);
    setQuery("");
    setOpen(false);
    input.current?.focus();
  }
  return (
    <div ref={root} className={`account-select ${span ? "span2" : ""}`}>
      <label htmlFor={id}>
        <span>
          {label}
          {required && <small> *</small>}
        </span>
      </label>
      <div className="account-select-control">
        <input
          ref={input}
          id={id}
          role="combobox"
          type="text"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          aria-activedescendant={
            open && matches[active] ? `${id}-option-${active}` : undefined
          }
          placeholder="Buscar por código o nombre…"
          required={required}
          value={editing ? query : selected?.label || ""}
          onFocus={(e) => {
            setOpen(true);
            setActive(0);
            e.currentTarget.select();
          }}
          onClick={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onChange={(e) => {
            setQuery(e.target.value);
            setEditing(true);
            setActive(0);
            setOpen(true);
            onChange("");
          }}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return;
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              setOpen(true);
              setActive((i) =>
                !open
                  ? e.key === "ArrowUp"
                    ? Math.max(0, matches.length - 1)
                    : 0
                  : matches.length
                    ? (i + (e.key === "ArrowDown" ? 1 : -1) + matches.length) %
                      matches.length
                    : 0,
              );
            } else if (e.key === "Enter" && open) {
              e.preventDefault();
              if (matches[active]) choose(matches[active]);
            } else if (e.key === "Escape" && open) {
              e.preventDefault();
              e.stopPropagation();
              setOpen(false);
              setEditing(false);
              setQuery("");
            } else if (e.key === "Tab") setOpen(false);
          }}
        />
        {(selected || query) && (
          <button
            type="button"
            className="account-select-clear"
            aria-label={`Limpiar ${label}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onChange("");
              setQuery("");
              setEditing(false);
              setActive(0);
              input.current?.focus();
              setOpen(true);
            }}
          >
            <X size={14} />
          </button>
        )}
        <ChevronDown
          size={15}
          className="account-select-chevron"
          aria-hidden="true"
        />
      </div>
      {open &&
        position &&
        createPortal(
          <ul
            ref={list}
            id={`${id}-list`}
            role="listbox"
            aria-label={`Cuentas: ${label}`}
            className="account-select-options"
            style={position}
          >
            {matches.length ? (
              matches.map((option, i) => (
                <li
                  key={option.value}
                  id={`${id}-option-${i}`}
                  role="option"
                  aria-selected={String(option.value) === String(value)}
                  className={i === active ? "active" : ""}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(option)}
                >
                  {option.label}
                </li>
              ))
            ) : (
              <li role="presentation" className="account-select-empty">
                No se encontraron cuentas.
              </li>
            )}
          </ul>,
          document.body,
        )}
    </div>
  );
}
